/**
 * Jours d'absence : accès aux données et cache mémoire.
 *
 * Spec : docs/spec-deux-maisons.md
 * Logique pure et testable : src/utils/away.ts
 *
 * Le cache est un simple Set de dates par enfant, chargé au boot sur une
 * FENÊTRE BORNÉE avec les autres loaders, étendu mois par mois à la demande du
 * calendrier, et invalidé à chaque écriture. Les chemins de lecture
 * (série, planification du jour, calendrier) le consultent de façon SYNCHRONE :
 * `isTaskAvailableForDate` et `classifyDay` sont appelés en boucle serrée sur
 * des dizaines de jours, un aller-retour réseau par jour serait catastrophique
 * pour les bascules d'onglet.
 *
 * Conséquence assumée : un enfant dont les jours n'ont pas encore été chargés
 * est considéré présent, donc le comportement retombe exactement sur celui
 * d'aujourd'hui. Jamais de faux « absent ».
 */

import { supabase } from '@/lib/supabase'
import { addDays, todayStr } from '@/services/date.service'
import { monthKeyOf, monthsBetween, monthBounds, type AwayDaySet } from '@/utils/away'

export type AwaySource = 'manual' | 'repeat'

interface AwayDayRow {
  child_id: string
  date: string
  source: AwaySource
}

/**
 * Fenêtre chargée au boot, en jours autour d'aujourd'hui.
 *
 * Le chargement est BORNÉ, et ce n'est pas une micro-optimisation : sans borne,
 * la charge du démarrage grandit chaque année sans jamais redescendre. Un
 * enfant en garde alternée accumule ~182 lignes par an, donc ~900 après cinq
 * ans, et un foyer de deux enfants ferait transiter ~170 Ko à chaque boot à
 * froid pour de la donnée que plus rien ne lit.
 *
 * Les bornes ne sont pas arbitraires : 60 jours en arrière est exactement la
 * fenêtre du calcul de série (streak.service), la SEULE qui lise le passé ;
 * 200 jours en avant couvre les 182 jours projetés par « Répéter ce rythme »
 * plus une marge. Au-delà, seul le calendrier du drawer peut aller, et il
 * charge son mois à la demande.
 *
 * Rien n'est supprimé en base : l'historique reste entier, on cesse simplement
 * de l'envoyer au client.
 */
const LOOKBACK_DAYS = 60
const LOOKAHEAD_DAYS = 200

/** child_id → Set<'YYYY-MM-DD'>. Absent de la Map = jamais chargé. */
const cache = new Map<string, Set<string>>()
/** child_id → source par date, pour « Ne plus répéter ». */
const sources = new Map<string, Map<string, AwaySource>>()
/**
 * child_id → mois 'YYYY-MM' dont on sait qu'ils sont à jour.
 *
 * C'est la seule mesure fiable de « chargé » depuis que le chargement est
 * fenêtré : la présence d'une clé dans `cache` ne dit plus rien sur la couverture,
 * et un mois vide est indiscernable d'un mois jamais demandé sans ce registre.
 */
const loadedMonths = new Map<string, Set<string>>()

/** Abonnés notifiés à chaque mutation, pour que l'UI se recalcule. */
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) {
    try {
      listener()
    } catch (err) {
      console.error('[awayDays] listener error:', err)
    }
  }
}

export function onAwayDaysChanged(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Marquer un jour change ce qui est planifié ce jour-là (isTaskAvailableForDate
 * consulte ce cache), donc le snapshot de la journée devient périmé. On le
 * purge ici plutôt que dans chaque écran appelant : oublier cette invalidation
 * dans un seul appelant produirait une journée fantôme, avec des tâches
 * affichées pour un enfant déclaré absent.
 *
 * Import dynamique volontaire : dailyCompletion.service importe ce module pour
 * sa lecture synchrone, un import statique en retour fermerait le cycle.
 */
function invalidateDaysFor(childId: string, dates: string[]): void {
  void import('@/services/dailyCompletion.service')
    .then(({ invalidateDailyCompletionCache }) => {
      for (const date of dates) invalidateDailyCompletionCache(childId, date)
    })
    .catch(err => console.error('[awayDays] invalidation impossible:', err))
}

/**
 * Seul point d'écriture du cache, et il écrit TOUJOURS une nouvelle référence.
 *
 * Non négociable : muter le Set en place laisse `getAwayDaysSync` renvoyer le
 * MÊME objet, donc un `computed()` qui le retourne est considéré inchangé par
 * Vue (comparaison par référence) et ses dépendants ne sont jamais réexécutés.
 * Concrètement, le calendrier des jours d'absence ne se redessinait pas au tap :
 * la donnée changeait, l'écran non. Les ensembles font quelques centaines
 * d'entrées, la copie est gratuite.
 */
function writeSet(childId: string, next: ReadonlySet<string> | undefined): void {
  // undefined = l'enfant n'avait jamais été chargé. On efface la clé au lieu
  // d'écrire un ensemble vide, sinon un rollback le ferait passer pour
  // « chargé, aucune absence » et ensureLoaded n'irait plus jamais le chercher.
  // Le registre des mois part avec, pour la même raison.
  if (next === undefined) {
    cache.delete(childId)
    loadedMonths.delete(childId)
    return
  }
  cache.set(childId, new Set(next))
}

/**
 * Lecture SYNCHRONE depuis le cache. C'est la fonction que consomment les
 * chemins chauds (série, planification). Un enfant inconnu retourne undefined,
 * ce que `isAwayOn` interprète comme « présent ».
 *
 * La référence retournée change à CHAQUE mutation (voir writeSet), ce qui rend
 * les `computed()` qui la relaient correctement réactifs.
 */
export function getAwayDaysSync(childId: string): AwayDaySet | undefined {
  return cache.get(childId)
}

/** Mois de la fenêtre chargée au boot, celle dont dépend le calcul de série. */
function bootWindowMonths(): string[] {
  const today = todayStr()
  return monthsBetween(addDays(today, -LOOKBACK_DAYS), addDays(today, LOOKAHEAD_DAYS))
}

function isMonthLoaded(childId: string, month: string): boolean {
  return loadedMonths.get(childId)?.has(month) ?? false
}

/** La fenêtre de base de cet enfant est-elle couverte ? */
export function hasLoadedAwayDays(childId: string): boolean {
  return bootWindowMonths().every(month => isMonthLoaded(childId, month))
}

export function getAwaySourceSync(childId: string, date: string): AwaySource | undefined {
  return sources.get(childId)?.get(date)
}

/**
 * Fusionne un lot de lignes dans le cache pour les mois DEMANDÉS seulement.
 *
 * On ne peut plus repartir d'un ensemble vide comme du temps du chargement
 * total : effacer tout à chaque requête ferait disparaître la fenêtre du boot
 * dès que le parent fait défiler le calendrier vers un mois lointain. On purge
 * donc les dates des mois rechargés, et on laisse le reste intact.
 *
 * La purge est nécessaire dans l'autre sens aussi : sans elle, une absence
 * supprimée depuis un autre appareil survivrait indéfiniment en mémoire.
 */
function applyRows(childIds: string[], months: string[], rows: AwayDayRow[]): void {
  const refreshed = new Set(months)

  for (const childId of childIds) {
    // Nouvelle référence obligatoire (copy-on-write) : voir writeSet.
    const kept = new Set<string>()
    for (const date of cache.get(childId) ?? []) {
      if (!refreshed.has(monthKeyOf(date))) kept.add(date)
    }
    cache.set(childId, kept)

    const srcMap = sources.get(childId) ?? new Map<string, AwaySource>()
    for (const date of [...srcMap.keys()]) {
      if (refreshed.has(monthKeyOf(date))) srcMap.delete(date)
    }
    sources.set(childId, srcMap)

    const loaded = loadedMonths.get(childId) ?? new Set<string>()
    for (const month of months) loaded.add(month)
    loadedMonths.set(childId, loaded)
  }

  for (const row of rows) {
    cache.get(row.child_id)?.add(row.date)
    sources.get(row.child_id)?.set(row.date, row.source)
  }
}

/** Dédup des chargements en vol, pour ne pas lancer N requêtes au boot. */
const inflight = new Map<string, Promise<void>>()

export const awayDaysService = {
  /**
   * Garantit que les jours d'un enfant sont en cache avant un calcul de série.
   * No-op si déjà chargé, donc gratuit après le boot.
   *
   * Le cache seul ne suffit pas ici : un calcul de série qui tournerait avant
   * l'hydratation verrait l'enfant « présent » partout et casserait une série
   * que le parent avait explicitement protégée, avant de la réparer au
   * rechargement suivant. Un aller-retour ponctuel vaut mieux que ce
   * clignotement.
   */
  async ensureLoaded(childId: string): Promise<void> {
    if (hasLoadedAwayDays(childId)) return
    const pending = inflight.get(childId)
    if (pending) return pending
    const promise = this.loadForChildren([childId]).finally(() => inflight.delete(childId))
    inflight.set(childId, promise)
    return promise
  },

  /**
   * Charge la fenêtre de base de plusieurs enfants en UNE requête. Appelé par
   * l'hydratation du foyer au boot. Coût constant dans le temps, contrairement
   * à la version non bornée : voir LOOKBACK_DAYS / LOOKAHEAD_DAYS.
   */
  async loadForChildren(childIds: string[]): Promise<void> {
    return this.loadMonths(childIds, bootWindowMonths())
  },

  /**
   * Charge des mois précis. Sert au calendrier du drawer, seul écran capable de
   * sortir de la fenêtre de base, et qui appelle donc à chaque changement de
   * mois. No-op si tous les mois demandés sont déjà couverts pour tous les
   * enfants concernés, donc gratuit en navigation normale.
   */
  async loadMonths(childIds: string[], months: string[]): Promise<void> {
    if (!childIds.length || !months.length) return

    const wanted = [...new Set(months)].sort()
    const missing = wanted.filter(month => childIds.some(id => !isMonthLoaded(id, month)))
    if (!missing.length) return

    // Une seule requête sur l'intervalle couvrant les mois manquants. Les mois
    // déjà connus pris au milieu sont refetchés de toute façon : on les marque
    // donc à jour eux aussi plutôt que de les redemander plus tard pour rien.
    const span = monthsBetween(missing[0], missing[missing.length - 1])
    const from = monthBounds(span[0]).start
    const to = monthBounds(span[span.length - 1]).end

    try {
      const { data, error } = await supabase
        .from('child_away_days' as any)
        .select('child_id, date, source')
        .in('child_id', childIds)
        .gte('date', from)
        .lte('date', to)

      if (error) {
        // On sort SANS marquer les mois chargés : l'appel suivant réessaiera,
        // là où un marquage optimiste laisserait un trou permanent.
        console.error('[awayDays.loadMonths] error:', error)
        return
      }
      applyRows(childIds, span, (data ?? []) as unknown as AwayDayRow[])
      notify()
    } catch (err) {
      console.error('[awayDays.loadMonths] exception:', err)
    }
  },

  /** Marque des jours comme absences. Idempotent (upsert sur la PK). */
  async markAway(childId: string, dates: string[], source: AwaySource = 'manual'): Promise<void> {
    if (!dates.length) return

    // Optimiste : l'UI doit répondre au tap sans attendre le réseau.
    // COPIE, jamais de mutation en place : voir writeSet().
    const previous = cache.get(childId)
    const next = new Set(previous ?? [])
    const srcMap = sources.get(childId) ?? new Map<string, AwaySource>()
    for (const date of dates) {
      next.add(date)
      srcMap.set(date, source)
    }
    writeSet(childId, next)
    sources.set(childId, srcMap)
    notify()

    try {
      const { error } = await supabase
        .from('child_away_days' as any)
        .upsert(
          dates.map(date => ({ child_id: childId, date, source })) as any,
          { onConflict: 'child_id,date' },
        )
      if (error) throw error
      invalidateDaysFor(childId, dates)
    } catch (err) {
      console.error('[awayDays.markAway] error, rollback:', err)
      writeSet(childId, previous)
      notify()
      throw err
    }
  },

  /** Retire des jours d'absence (l'enfant est là, finalement). */
  async markPresent(childId: string, dates: string[]): Promise<void> {
    if (!dates.length) return

    const previous = cache.get(childId)
    const next = new Set(previous ?? [])
    for (const date of dates) next.delete(date)
    writeSet(childId, next)
    notify()

    try {
      const { error } = await supabase
        .from('child_away_days' as any)
        .delete()
        .eq('child_id', childId)
        .in('date', dates)
      if (error) throw error
      const srcMap = sources.get(childId)
      if (srcMap) for (const date of dates) srcMap.delete(date)
      invalidateDaysFor(childId, dates)
    } catch (err) {
      console.error('[awayDays.markPresent] error, rollback:', err)
      writeSet(childId, previous)
      notify()
      throw err
    }
  },

  /** Bascule un jour. Retourne l'état APRÈS bascule (true = absent). */
  async toggle(childId: string, date: string): Promise<boolean> {
    const wasAway = cache.get(childId)?.has(date) ?? false
    if (wasAway) {
      await this.markPresent(childId, [date])
      return false
    }
    await this.markAway(childId, [date], 'manual')
    return true
  },

  /**
   * « Ne plus répéter » : retire les jours PROJETÉS à partir de `from`, en
   * laissant intactes les corrections manuelles. C'est la seule raison d'être
   * de la colonne `source`.
   */
  async clearProjected(childId: string, from: string): Promise<void> {
    const srcMap = sources.get(childId)
    const toRemove: string[] = []
    if (srcMap) {
      for (const [date, source] of srcMap) {
        if (source === 'repeat' && date >= from) toRemove.push(date)
      }
    }

    const previous = cache.get(childId)
    const next = new Set(previous ?? [])
    for (const date of toRemove) next.delete(date)
    writeSet(childId, next)
    notify()

    try {
      const { error } = await supabase
        .from('child_away_days' as any)
        .delete()
        .eq('child_id', childId)
        .eq('source', 'repeat')
        .gte('date', from)
      if (error) throw error
      if (srcMap) for (const date of toRemove) srcMap.delete(date)
      invalidateDaysFor(childId, toRemove)
    } catch (err) {
      console.error('[awayDays.clearProjected] error, rollback:', err)
      writeSet(childId, previous)
      notify()
      throw err
    }
  },

  /** Y a-t-il des jours projetés à venir ? Gouverne l'affichage de « Ne plus répéter ». */
  hasProjectedFrom(childId: string, from: string): boolean {
    const srcMap = sources.get(childId)
    if (!srcMap) return false
    for (const [date, source] of srcMap) {
      if (source === 'repeat' && date >= from) return true
    }
    return false
  },

  /** Vide le cache (changement de compte, de foyer). */
  reset(): void {
    cache.clear()
    sources.clear()
    loadedMonths.clear()
    notify()
  },
}
